import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode, HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError, EMPTY } from 'rxjs';
import { NotificationService } from './notification.service';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { EcklerRouteConfig } from '../eckler-route-config';

@Injectable({
  providedIn: 'root'
})

export class AuthInterceptor implements HttpInterceptor {

  // Injecting Injector instead of authservice to avoid circular dependency issues
  constructor(private injector: Injector, private _notification: NotificationService, private router: Router) {
  }

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private getToken(): string | null {
    try {
      const ecklerUser = localStorage.getItem('ecklerUser');
      if (ecklerUser) {
        const user = JSON.parse(ecklerUser);
        return user.token;
      }
    } catch (error) {
      console.error('Error parsing user token from localStorage:', error);
    }
    return null;
  }

  private getRefreshToken(): string | null {
    try {
      const ecklerUser = localStorage.getItem('ecklerUser');
      if (ecklerUser) {
        const user = JSON.parse(ecklerUser);
        return user.refreshToken;
      }
    } catch (error) {
      console.error('Error parsing refresh token from localStorage:', error);
    }
    return null;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    
    const authToken = req.headers.get('Authorization');
    const currentToken = this.getToken();

    // Skip token injection for public endpoints
    const publicEndpoints = ['/security/refreshtoken', '/security/register-callback', '/security/register-validate'];
    if (publicEndpoints.some(endpoint => req.url.includes(endpoint))) {
      return next.handle(req);
    }

    if (currentToken && (authToken === null || authToken === undefined)) {
      req = this.addTokenToRequest(req, currentToken);
    }

    return next.handle(req).pipe(
      catchError((err) => {
        const errorMsg = err.error?.message || err.error;
        switch (err.status) {
          case 0: {
            break;
          }

          case HttpStatusCode.Locked: {
            this._notification.showErrorMessage('Your account has been locked because there have been too many failed login attempts. If you haven’t tried logging in before, contact your administrator. Otherwise, come back later.');
            break;
          }

          case HttpStatusCode.Unauthorized: {
            return this.handle401Error(req, next);
          }
          case HttpStatusCode.Forbidden: {
            this._notification.showErrorMessage('You cannot do this action');

            break;
          }
          case HttpStatusCode.BadRequest: {
            if (err && err.error) {
              let errorMsg = "";
              if (typeof err.error === 'string') {
                errorMsg = err.error;
              } else if (err.error.message) {
                errorMsg = err.error.message;
              } else if (Array.isArray(err.error.errors)) {
                errorMsg = err.error.errors.map((e: any) => e.message).join(' ');
              } else {
                errorMsg = 'Bad Request';
              }
              this._notification.showErrorMessage(errorMsg);
            } else {
              this._notification.showErrorMessage('The request was not successful, please try again');
            }
            break;
          }
          default:
            break;
        }

        return throwError(() => err);

      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        this.isRefreshing = false;
        localStorage.removeItem('ecklerUser');
        this.router.navigate([EcklerRouteConfig.loginRoute('')]);
        return throwError(() => new Error('Please log in again to proceed.'));
      }

      const httpClient = this.injector.get(HttpClient);
      return httpClient.post<any>(`${environment.mainApiUrl}security/refreshtoken`, {
        refreshToken: refreshToken
      }).pipe(
        switchMap((response: any) => {
          this.isRefreshing = false;
          this.updateStoredTokens(response.token, response.refreshToken);
          this.refreshTokenSubject.next(response.token);
          return next.handle(this.addTokenToRequest(request, response.token));
        }),
        catchError((error) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
          localStorage.removeItem('ecklerUser');
          this.router.navigate([EcklerRouteConfig.loginRoute('')]);
          return throwError(() => new Error('Please log in again to proceed.'));
        }));

    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(jwt => next.handle(this.addTokenToRequest(request, jwt))));
    }
  }

  private updateStoredTokens(token: string, refreshToken: string): void {
    try {
      const ecklerUser = localStorage.getItem('ecklerUser');
      if (ecklerUser) {
        const user = JSON.parse(ecklerUser);
        user.token = token;
        user.refreshToken = refreshToken;
        localStorage.setItem('ecklerUser', JSON.stringify(user));
      }
    } catch (error) {
      console.error('Error updating stored tokens:', error);
    }
  }
  private addTokenToRequest(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }
}
